import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Envelope, EnvelopeItem } from './envelope.model';
import { EnvelopeService } from './envelope.service';
import { WaiverBuilderService } from './waiver-builder.service';
import { DEFAULT_MINOR_AGE_THRESHOLD } from './waiver-builder.model';
import {
  DateOfBirthProblem,
  ageOn,
  deriveIsMinorFromDateOfBirth,
  blocksSaveDateOfBirth,
  validateDateOfBirth,
} from './waiver-compliance';

interface AddParticipantForm {
  name: string;
  email: string;
  phone: string;
  /** Calendar date of birth, `YYYY-MM-DD`. Optional; without it the
   *  participant is classified as an adult. */
  dateOfBirth: string;
  /** DERIVED from `dateOfBirth` + the company threshold, never entered
   *  directly. Kept on the form so the guardian field and the email label
   *  keep reading one flag. */
  isMinor: boolean;
  guardianName: string;
}

@Component({
  selector: 'app-envelope-sign',
  templateUrl: './envelope-sign.component.html',
  styleUrls: ['./envelope-sign.component.scss']
})
export class EnvelopeSignComponent implements OnInit, OnDestroy {

  protected envelope: Envelope | null = null;
  protected loading = true;
  protected notFound = false;
  private envelopeId = '';
  private sub?: Subscription;

  /** Every envelope attached to this envelope's booking, live from Firestore. */
  protected bookingEnvelopes: Envelope[] = [];
  private bookingSub?: Subscription;

  protected showAddForm = false;
  /**
   * Company minor-age threshold used to classify a participant added here.
   * Loaded from `WaiverBuilderService` against `envelope.companyId` — the
   * same single source of truth the checkout and partner-side flows use.
   * `companies` is world-readable (firestore.rules), which is what lets
   * this public signing page resolve it at all; the public per-waiver sign
   * page already loads it the same way.
   *
   * Seeded with the shared default only so the form renders before the
   * load resolves. It is never used to CLASSIFY without a successful read —
   * `saveAdd` refuses instead (see `minorAgeThresholdReadFailed`).
   */
  protected minorAgeThreshold: number = DEFAULT_MINOR_AGE_THRESHOLD;
  /** Suppresses the date-of-birth error until the field has been visited,
   *  so opening the form doesn't greet the customer with a red "required". */
  protected addDobTouched = false;
  /** Picker has some parts chosen but not all three — see the picker's
   *  `partialChange`. Blank saves; partial must not. */
  protected addDobPartial = false;
  protected addForm: AddParticipantForm = this.emptyAddForm();
  protected adding = false;
  protected addError = '';
  protected lastAddedMessage = '';
  /** Tracks the success-message fadeout so it can be cancelled on destroy. */
  private lastAddedTimer?: ReturnType<typeof setTimeout>;
  /** Set when an envelope is created but its email dispatch fails. Drives
   *  the inline Copy Link button next to the flash banner so the partner
   *  can hand the link to the signer without re-opening anything. */
  protected failedEnvelopeId: string | null = null;
  /** Brief "Copied!" feedback after a successful clipboard write. */
  protected linkCopied = false;
  private linkCopiedTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private envelopeService: EnvelopeService,
    private waiverBuilderService: WaiverBuilderService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.envelopeId = this.route.snapshot.paramMap.get('envelopeId') || '';

    if (!this.envelopeId) {
      this.loading = false;
      this.notFound = true;
      return;
    }

    // Initial cache-first read covers the dev-seeded test envelopes that
    // exist only in memory and won't appear in the Firestore stream below.
    this.loadEnvelope();

    // Live Firestore subscription — every CF write re-emits, so the checklist
    // updates instantly when any participant (this tab, another tab, another
    // device) signs an item. Ignore `undefined` emissions: dev-seeded
    // envelopes have no Firestore doc, and `loadEnvelope` already handled
    // the genuinely-missing case.
    this.sub = this.envelopeService.streamEnvelopeById(this.envelopeId).subscribe({
      next: env => {
        if (!env) return;
        // The Firestore stream can emit outside Angular's zone (AngularFire
        // compat path), which leaves the bound view stale after a sibling
        // tab signs an item — the data updates but no CD pass fires. Re-
        // enter the zone + markForCheck so the checklist repaints as soon
        // as the server confirms the new state.
        this.zone.run(() => {
          this.envelope = env;
          this.loading = false;
          this.notFound = false;
          this.maybeStartBookingStream(env);
          this.cdr.markForCheck();
        });
      },
      error: err => {
        console.error('Failed to stream envelope:', err);
      },
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.bookingSub?.unsubscribe();
    if (this.lastAddedTimer) clearTimeout(this.lastAddedTimer);
    if (this.linkCopiedTimer) clearTimeout(this.linkCopiedTimer);
  }

  /** Copies the failed-envelope's public signing link to the clipboard so
   *  the partner can hand it to the signer manually. Bound to the inline
   *  icon button shown next to the failure flash. */
  protected async copyEnvelopeLink(): Promise<void> {
    if (!this.failedEnvelopeId) return;
    const link = this.envelopeService.generateEnvelopeLink(this.failedEnvelopeId);
    try {
      await navigator.clipboard.writeText(link);
      this.linkCopied = true;
      if (this.linkCopiedTimer) clearTimeout(this.linkCopiedTimer);
      this.linkCopiedTimer = setTimeout(() => {
        this.linkCopied = false;
        this.linkCopiedTimer = undefined;
      }, 1500);
    } catch (err) {
      // Clipboard API can fail in non-secure contexts or when permissions
      // are denied. Surface the URL via prompt() as a manual fallback —
      // the user can Ctrl+C the pre-filled value.
      console.error('Clipboard write failed; falling back to prompt:', err);
      window.prompt('Copy this signing link:', link);
    }
  }

  private async loadEnvelope(): Promise<void> {
    const env = await this.envelopeService.getEnvelopeById(this.envelopeId);
    if (!env) {
      this.loading = false;
      this.notFound = true;
      this.envelope = null;
      return;
    }
    this.envelope = env;
    this.loading = false;
    this.notFound = false;
    this.maybeStartBookingStream(env);
  }

  /**
   * Primary-only: subscribe to every envelope on this booking so the
   * "Other participants" list and the guardian-name autocomplete have data.
   * Idempotent — only attaches once per envelope-sign mount.
   */
  private maybeStartBookingStream(env: Envelope): void {
    if (!this.isPrimary || !env.bookingId || this.bookingSub) return;
    this.bookingSub = this.envelopeService.streamEnvelopesByBookingId(env.bookingId)
      .subscribe({
        next: envs => {
          // Same zone re-entry rationale as the per-envelope stream above —
          // status/progress on the "Other participants" rows must repaint
          // when a sibling envelope is signed.
          this.zone.run(() => {
            this.bookingEnvelopes = envs;
            this.cdr.markForCheck();
          });
        },
        error: err => {
          console.error('Failed to stream booking envelopes:', err);
        },
      });
  }

  protected openItem(item: EnvelopeItem): void {
    if (item.status === 'signed' || !this.envelope) return;

    // Prefill comes from the envelope participant — WaiverSignComponent
    // re-resolves it server-side via envelopeId/itemId, so these URL params
    // are hints for display only.
    const { participant } = this.envelope;
    const queryParams: { [key: string]: string } = {
      envelopeId: this.envelopeId,
      itemId: item.itemId,
    };
    if (participant.name) queryParams['name'] = participant.name;
    if (participant.email) queryParams['email'] = participant.email;
    if (participant.phone) queryParams['phone'] = participant.phone;

    this.router.navigate(['/sign-waiver', item.waiverId], { queryParams });
  }

  protected get signedCount(): number {
    return this.envelope?.items.filter(i => i.status === 'signed').length ?? 0;
  }

  protected get totalCount(): number {
    return this.envelope?.items.length ?? 0;
  }

  protected get progressPercent(): number {
    if (this.totalCount === 0) return 0;
    return Math.round((this.signedCount / this.totalCount) * 100);
  }

  protected get currentYear(): number {
    return new Date().getFullYear();
  }

  // --- Add-another-participant (primary only) ---

  protected get isPrimary(): boolean {
    return this.envelope?.participant.role === 'Primary';
  }

  protected get otherParticipants(): Envelope[] {
    if (!this.envelope) return [];
    return this.bookingEnvelopes.filter(e => e.id !== this.envelope!.id);
  }

  /** Non-minor participant names on this booking, for the guardian autocomplete. */
  protected guardianSuggestions(): string[] {
    return this.bookingEnvelopes
      .filter(e => !e.participant.isMinor && !!e.participant.name.trim())
      .map(e => e.participant.name.trim());
  }

  protected openAddForm(): void {
    this.addForm = this.emptyAddForm();
    this.addError = '';
    this.addDobTouched = false;
    this.addDobPartial = false;
    this.showAddForm = true;
    // Warm the threshold so the form can classify a typed date of birth
    // live. The authoritative read happens again in `saveAdd`.
    void this.refreshMinorAgeThreshold();
  }

  /**
   * Loads the company's configured minor-age threshold for this envelope.
   * Best-effort here (the form just needs something to show); `saveAdd`
   * re-reads it and refuses to classify if the read failed.
   *
   * Takes the company id explicitly so callers mid-`await` use the envelope
   * they captured rather than `this.envelope`, which the live stream (or
   * the initial cache read) can replace underneath them.
   */
  private async refreshMinorAgeThreshold(companyId?: string): Promise<void> {
    companyId = companyId || this.envelope?.companyId;
    if (!companyId) return;
    try {
      await this.waiverBuilderService.loadMinorAgeThreshold(companyId, true);
      this.minorAgeThreshold = this.waiverBuilderService.getMinorAgeThreshold();
    } catch (err) {
      console.error('Could not load the company minor-age threshold:', err);
    }
  }

  protected cancelAdd(): void {
    this.addDobPartial = false;
    if (this.adding) return;
    this.showAddForm = false;
    this.addForm = this.emptyAddForm();
    this.addError = '';
  }

  /**
   * Date of birth changed — re-derive the classification and apply the same
   * rule the old "Is a minor" checkbox drove. The customer states a fact;
   * Fleetmaid decides whether it is a minor, against the shop's configured
   * threshold.
   *
   * An unusable date leaves the form classified as adult, but `canSaveAdd`
   * blocks the save, so no guessed classification reaches an envelope.
   */
  protected onAddDobChange(): void {
    const wasMinor = this.addForm.isMinor;
    this.addForm.isMinor = deriveIsMinorFromDateOfBirth(
      this.addForm.dateOfBirth,
      this.minorAgeThreshold,
    ) === true;
    // Only react on an actual flip, so correcting a typo inside the same
    // bracket doesn't wipe a guardian name already entered.
    if (this.addForm.isMinor === wasMinor) return;
    if (!this.addForm.isMinor) this.addForm.guardianName = '';
  }

  /**
   * Age implied by the date currently entered, or null when it isn't a
   * usable date yet. Echoed under the field because a mistyped YEAR is the
   * common error and otherwise silent — it produces a valid date, a wrong
   * classification, and no feedback.
   */
  protected get addDobAge(): number | null {
    const value = (this.addForm.dateOfBirth || '').trim();
    if (!value || validateDateOfBirth(value) !== null) return null;
    return ageOn(value, new Date());
  }

  /**
   * Inline validation message for the date-of-birth field.
   *
   * Reports nothing for a BLANK field — date of birth is optional, so
   * leaving it empty is a valid choice rather than an error. A typo still
   * reports.
   */
  protected get addDobError(): string {
    if (this.addDobPartial) return 'Finish the date of birth or clear it.';
    const problem: DateOfBirthProblem | null = validateDateOfBirth(this.addForm.dateOfBirth);
    if (problem === null || problem === 'missing') return '';
    if (problem === 'future') return "Date of birth can't be in the future.";
    return 'Enter a valid date of birth.';
  }

  protected get canSaveAdd(): boolean {
    const f = this.addForm;
    if (!f.name.trim()) return false;
    // Date of birth is OPTIONAL — a customer without it to hand is not
    // blocked. A typo still is: see `blocksSaveDateOfBirth`, which
    // documents the consequence of saving without one (the participant is
    // recorded as an adult and receives the adult waiver variant).
    if (this.addDobPartial) return false;
    if (blocksSaveDateOfBirth(f.dateOfBirth)) return false;
    if (f.isMinor && !f.guardianName.trim()) return false;
    // Need at least an email OR phone so the envelope can actually be delivered.
    if (!f.email.trim() && !f.phone.trim()) return false;
    // If phone was typed at all, require ≥10 digits — otherwise the envelope
    // is created with an unusable phone that we later route around anyway.
    if (f.phone.trim() && this.isInvalidPhone(f.phone)) return false;
    // Same treatment for email — a typed value has to look like an email
    // shape or SparkPost silently rejects it and the participant never
    // receives their envelope link.
    if (f.email.trim() && this.isInvalidEmail(f.email)) return false;
    return true;
  }

  /**
   * Phone is valid when it contains at least 10 digits after stripping
   * non-digit characters — accommodates `(555) 555-5555`, `+1 555 555 5555`,
   * etc. Empty is treated as valid (phone is optional here as long as email
   * is provided; the both-blank case is caught by `canSaveAdd` above).
   */
  protected isInvalidPhone(value: string): boolean {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;
    const digits = trimmed.replace(/\D/g, '');
    return digits.length < 10;
  }

  /**
   * Basic email format check — `local@domain.tld` shape. Empty is valid;
   * `canSaveAdd` handles the "must have one of email/phone" requirement
   * separately. Matches the pattern used across the app (cart, rental-
   * waivers Add Participant, etc.) so validation feels consistent.
   */
  protected isInvalidEmail(value: string): boolean {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;
    return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  }

  /** Inline error message helper for the phone field's `<small>` slot. */
  protected get addPhoneError(): string {
    return this.isInvalidPhone(this.addForm.phone)
      ? 'Enter a phone number with at least 10 digits.'
      : '';
  }

  /** Inline error message helper for the email field's `<small>` slot. */
  protected get addEmailError(): string {
    return this.isInvalidEmail(this.addForm.email)
      ? 'Enter a valid email address (e.g. name@example.com).'
      : '';
  }

  protected async saveAdd(): Promise<void> {
    if (!this.canSaveAdd || !this.envelope || !this.envelope.bookingId) return;

    this.adding = true;
    this.addError = '';

    const primary = this.envelope;
    const form = this.addForm;
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();

    // Under the group model, each existing item's `waiverId` points at a
    // specific resolved VARIANT for the primary participant (usually the
    // adult variant). Cloning them 1:1 for a MINOR would carry the adult
    // variant along — losing the group's minor swap. Walk each item
    // waiver → group → correct variant based on `form.isMinor`, and
    // de-dup by resolved id so two items in the same group don't
    // produce two rows on the new envelope.
    //
    // Old code appended a company-singleton Minor Waiver on top; that
    // flag is retired (see WaiverBuilderService.saveWaiver) — the
    // per-group `minorWaiverId` swap replaces it.
    try {
      await this.waiverBuilderService.loadCompanyWaivers(primary.companyId);
      await this.waiverBuilderService.loadCompanyWaiverGroups(primary.companyId);
    } catch (err) {
      // Non-fatal — resolver falls back to the source id when the
      // group cache is empty (same behavior as the legacy path).
      console.error('Failed to hydrate builder caches for Add Participant:', err);
    }

    const dateOfBirth = (form.dateOfBirth || '').trim();

    // Authoritative threshold read for THIS participant, before any
    // classification happens.
    await this.refreshMinorAgeThreshold(primary.companyId);
    if (dateOfBirth && this.waiverBuilderService.minorAgeThresholdReadFailed(primary.companyId)) {
      // Refuse rather than classify against the fallback: persisting
      // `minorAgeThresholdAtIssue: 18` when 18 was a guess would bake a
      // wrong historical fact into the envelope, and a shop configured at
      // 21 would silently get the adult waiver for one of its minors.
      //
      // Only when a date of birth was actually given. With none there is
      // nothing to classify and no threshold is consulted or stored, so a
      // failed read is irrelevant — blocking on it would refuse a save that
      // never needed the value.
      this.addError = 'We could not confirm this shop\'s waiver age settings. Please try again in a moment.';
      this.adding = false;
      this.zone.run(() => this.cdr.markForCheck());
      return;
    }

    // Classification is DERIVED, never asserted by the customer. One date
    // of birth + one threshold yields one classification, and therefore one
    // waiver variant below.
    const minorAgeThresholdAtIssue = this.minorAgeThreshold;
    const isMinor = deriveIsMinorFromDateOfBirth(
      dateOfBirth,
      minorAgeThresholdAtIssue,
    ) === true;
    form.isMinor = isMinor;

    const seenWaiverIds = new Set<string>();
    const items: EnvelopeItem[] = [];
    for (const source of primary.items) {
      if (source.waiverType === 'legacy') continue;
      const resolvedId = this.waiverBuilderService
        .resolveWaiverForParticipant(source.waiverId, isMinor) ?? source.waiverId;
      if (seenWaiverIds.has(resolvedId)) continue;
      seenWaiverIds.add(resolvedId);
      // Prefer the resolved variant's own name (loaded via
      // savedWaivers$) over the source item's `waiverName`, since the
      // source name refers to the primary's variant.
      const resolvedWaiver = this.waiverBuilderService.savedWaivers$.getValue()
        .find(w => w.id === resolvedId);
      items.push({
        itemId: this.envelopeService.generateItemId(),
        waiverId: resolvedId,
        waiverType: source.waiverType,
        waiverName: resolvedWaiver?.name || source.waiverName,
        status: 'pending',
      });
    }

    try {
      const envelope = await this.envelopeService.createEnvelope({
        companyId: primary.companyId,
        bookingId: primary.bookingId,
        bookingLabel: primary.bookingLabel,
        participant: {
          name,
          email: email || undefined,
          phone: phone || undefined,
          role: isMinor ? 'Minor' : 'Guest',
          // The durable fact and the threshold that derived the
          // classification, written together so the envelope records not
          // just WHAT this participant was classified as but WHY.
          //
          // Both omitted together when no date of birth was given:
          // `currentClassification` requires both, so omitting them keeps
          // this participant out of age-transition evaluation rather than
          // judging them against a date nobody supplied.
          ...(dateOfBirth ? { dateOfBirth, minorAgeThresholdAtIssue } : {}),
          isMinor,
          guardianName: isMinor ? form.guardianName.trim() : undefined,
        },
        recipientEmail: email || undefined,
        recipientPhone: phone || undefined,
        items,
      });

      // The createEnvelope CF arrayUnions onto rental.envelopeIds in the
      // same transaction — no separate addEnvelopeIdToRental call needed.

      // Fire the email (best effort). Route to guardian for minors.
      this.failedEnvelopeId = null;
      if (email) {
        const recipientName = isMinor && form.guardianName
          ? form.guardianName.trim()
          : name;
        try {
          await this.envelopeService.sendEnvelopeEmail(envelope.id, email, recipientName);
          this.lastAddedMessage = `Envelope sent to ${email}`;
        } catch (err) {
          console.error('Failed to email new envelope:', err);
          this.lastAddedMessage = `Envelope created but email failed — share the link manually with ${name}.`;
          // Surface the link inline next to the flash via failedEnvelopeId
          // so the partner can copy it with one click. (Previously we
          // opened a Swal here, but the inline button is less intrusive
          // and the flash banner already conveys the failure.)
          this.failedEnvelopeId = envelope.id;
        }
      } else {
        this.lastAddedMessage = `Envelope created for ${name}. No email on file — share the signing link manually.`;
        this.failedEnvelopeId = envelope.id;
      }

      if (this.lastAddedTimer) clearTimeout(this.lastAddedTimer);
      this.lastAddedTimer = setTimeout(() => {
        this.lastAddedMessage = '';
        this.failedEnvelopeId = null;
        this.lastAddedTimer = undefined;
      }, 5000);
      this.showAddForm = false;
      this.addForm = this.emptyAddForm();
    } catch (err) {
      console.error('Failed to create envelope:', err);
      this.addError = 'Failed to add participant. Please try again.';
    } finally {
      this.adding = false;
    }
  }

  private emptyAddForm(): AddParticipantForm {
    return { name: '', email: '', phone: '', dateOfBirth: '', isMinor: false, guardianName: '' };
  }
}
